<template>
  <div class="currency-input">
    <div class="input-group">
      <div class="input-group-prepend">
        <span class="input-group-text">{{ currency }}</span>
      </div>
      <input
        ref="input"
        type="text"
        class="form-control"
        :class="{ 'is-invalid': hasError }"
        :placeholder="placeholder"
        :value="displayValue"
        :disabled="disabled"
        :readonly="readonly"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown="handleKeydown"
      />
    </div>
    <div v-if="hasError && errorMessage" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { formatCurrency, parseCurrency } from '../../utils/formatters';

export default {
  name: 'CurrencyInput',
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    currency: {
      type: String,
      default: '₦',
    },
    placeholder: {
      type: String,
      default: '0.00',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: null,
    },
    decimals: {
      type: Number,
      default: 2,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isFocused: false,
      internalValue: '',
    };
  },
  computed: {
    displayValue() {
      if (this.isFocused) {
        return this.internalValue;
      }

      const numericValue = this.getNumericValue();
      if (numericValue === 0 && !this.value) {
        return '';
      }

      return this.formatForDisplay(numericValue);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.internalValue = this.formatForInput(newValue);
      },
    },
  },
  methods: {
    getNumericValue() {
      if (typeof this.value === 'number') {
        return this.value;
      }
      return parseCurrency(this.value);
    },

    formatForDisplay(value) {
      if (!value && value !== 0) return '';
      return Number(value).toLocaleString('en-US', {
        minimumFractionDigits: this.decimals,
        maximumFractionDigits: this.decimals,
      });
    },

    formatForInput(value) {
      if (!value && value !== 0) return '';
      const numeric = typeof value === 'number' ? value : parseCurrency(value);
      return numeric.toString();
    },

    handleInput(event) {
      const inputValue = event.target.value;

      // Remove any non-numeric characters except decimal point
      const cleanValue = inputValue.replace(/[^0-9.]/g, '');

      // Ensure only one decimal point
      const parts = cleanValue.split('.');
      let formattedValue = parts[0];

      if (parts.length > 1) {
        // Limit decimal places
        const decimalPart = parts[1].substring(0, this.decimals);
        formattedValue = `${parts[0]}.${decimalPart}`;
      }

      this.internalValue = formattedValue;

      // Convert to number and validate
      const numericValue = parseFloat(formattedValue) || 0;

      // Apply min/max constraints
      if (this.min !== null && numericValue < this.min) {
        this.$emit('error', `Value cannot be less than ${formatCurrency(this.min, this.currency)}`);
        return;
      }

      if (this.max !== null && numericValue > this.max) {
        this.$emit('error', `Value cannot be more than ${formatCurrency(this.max, this.currency)}`);
        return;
      }

      this.$emit('input', numericValue);
      this.$emit('change', numericValue);
    },

    handleFocus(event) {
      this.isFocused = true;
      this.internalValue = this.formatForInput(this.value);
      this.$emit('focus', event);

      // Select all text on focus
      this.$nextTick(() => {
        event.target.select();
      });
    },

    handleBlur(event) {
      this.isFocused = false;
      this.$emit('blur', event);
    },

    handleKeydown(event) {
      // Allow: backspace, delete, tab, escape, enter
      if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1
          // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
          || (event.keyCode === 65 && event.ctrlKey === true)
          || (event.keyCode === 67 && event.ctrlKey === true)
          || (event.keyCode === 86 && event.ctrlKey === true)
          || (event.keyCode === 88 && event.ctrlKey === true)
          // Allow: home, end, left, right, down, up
          || (event.keyCode >= 35 && event.keyCode <= 40)) {
        return;
      }

      // Ensure that it is a number or decimal point and stop the keypress
      if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57))
          && (event.keyCode < 96 || event.keyCode > 105)
          && event.keyCode !== 190 && event.keyCode !== 110) {
        event.preventDefault();
      }

      // Only allow one decimal point
      if ((event.keyCode === 190 || event.keyCode === 110)
          && this.internalValue.indexOf('.') !== -1) {
        event.preventDefault();
      }
    },

    focus() {
      this.$refs.input.focus();
    },

    blur() {
      this.$refs.input.blur();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/variable.scss';

.currency-input {
  width: 100%;
}

.input-group {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  width: 100%;
}

.input-group-prepend {
  display: flex;
}

.input-group-text {
  display: flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  margin-bottom: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  color: $grey;
  text-align: center;
  white-space: nowrap;
  background-color: #EDF2F9;
  border: 1px solid #EDF2F9;
  border-radius: 8px 0 0 8px;
  border-right: none;
}

.form-control {
  border-radius: 0 8px 8px 0 !important;
  border-left: none !important;

  &:focus {
    border-left: none !important;
  }
}

.error-message {
  color: $primary;
  font-size: 12px;
  margin-top: 4px;
}
</style>
